import { FC, useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import { MessageCircleX, AlertTriangle, BookOpen, Loader2 } from 'lucide-react';
import { TQuizLearningProgress } from '../../../../types/analytics.types';
import AnalyticsService from '../../../../services/analytics.service';

const SKILL_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#84cc16',
];

interface QuizLearningProps {
  quizId: string;
}

const QuizLearning: FC<QuizLearningProps> = ({ quizId }) => {
  const [data, setData] = useState<TQuizLearningProgress | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    AnalyticsService.fetchQuizLearningProgress({ quizId })
      .then((res: { data: { data: TQuizLearningProgress | null } }) => {
        setData(res.data?.data ?? null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [quizId]);

  if (loading) {
    return (
      <div className="mt-4 w-full flex justify-center items-center py-20">
        <Loader2 className="animate-spin text-gray-400" size={40} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mt-4 w-full flex flex-col justify-center items-center py-20">
        <MessageCircleX color="#999" size={60} />
        <p className="text-[14px] font-medium text-appTextLight mt-3">
          No learning data available.
        </p>
      </div>
    );
  }

  const { masteryLevel, aiAssistLevel, skillsProgressData, learningOutcomesData, classLearningGaps } = data;

  // Skills progress chart traces
  const skillChartData = skillsProgressData.map((skillData, index) => ({
    x: skillData.weeklyData.map((w) => w.week),
    y: skillData.weeklyData.map((w) => w.avgCorrectPct),
    type: 'scatter' as const,
    mode: 'lines+markers' as const,
    name: skillData.skill,
    line: { color: SKILL_COLORS[index % SKILL_COLORS.length], width: 2 },
    marker: { color: SKILL_COLORS[index % SKILL_COLORS.length], size: 6 },
  }));

  return (
    <div className="mt-4">
      {/* Mastery Level + AI Assist Level pies */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="border-[#E2E8F0] border bg-white px-4 hover:border-[#020817] transition-all cursor-pointer rounded-2xl">
          <Plot
            data={[
              {
                type: 'pie',
                values: [
                  masteryLevel.unmet.count,
                  masteryLevel.met.count,
                  masteryLevel.exceeded.count,
                ],
                labels: ['Unmet', 'Met', 'Exceeded'],
                marker: { colors: ['#ef4444', '#f59e0b', '#10b981'] },
                textinfo: 'label+percent',
                hoverinfo: 'label+value+percent',
                textfont: { color: '#FFFFFF', family: 'Inter, system-ui, sans-serif', size: 12 },
                insidetextfont: { color: '#FFFFFF', family: 'Inter, system-ui, sans-serif', size: 12 },
              },
            ]}
            layout={{
              title: {
                text: 'Mastery Level',
                x: 0,
                xanchor: 'left',
                font: { size: 16, family: 'Inter, system-ui, sans-serif' },
                subtitle: { text: `${masteryLevel.totalStudents} students total` },
              },
              showlegend: true,
              legend: { orientation: 'h', yanchor: 'bottom', y: -0.3, xanchor: 'center', x: 0.5 },
              margin: { t: 80, b: 80, l: 20, r: 20 },
              plot_bgcolor: '#fff',
              paper_bgcolor: '#fff',
              autosize: true,
            }}
            className="h-[320px] w-full"
            style={{ width: '100%', maxWidth: '100%' }}
            config={{ responsive: true, displayModeBar: false }}
            useResizeHandler={true}
          />
        </div>

        <div className="border-[#E2E8F0] border bg-white px-4 hover:border-[#020817] transition-all cursor-pointer rounded-2xl">
          <Plot
            data={[
              {
                type: 'pie',
                values: [
                  aiAssistLevel.aiAssisted.count,
                  aiAssistLevel.noAiAssisted.count,
                ],
                labels: ['AI Assisted', 'No AI'],
                marker: { colors: ['#8784d4', '#91c89e'] },
                textinfo: 'label+percent',
                hoverinfo: 'label+value+percent',
                textfont: { color: '#FFFFFF', family: 'Inter, system-ui, sans-serif', size: 12 },
                insidetextfont: { color: '#FFFFFF', family: 'Inter, system-ui, sans-serif', size: 12 },
              },
            ]}
            layout={{
              title: {
                text: 'AI Assist Usage',
                x: 0,
                xanchor: 'left',
                font: { size: 16, family: 'Inter, system-ui, sans-serif' },
                subtitle: { text: `${aiAssistLevel.totalQuestions} total questions` },
              },
              showlegend: true,
              legend: { orientation: 'h', yanchor: 'bottom', y: -0.3, xanchor: 'center', x: 0.5 },
              margin: { t: 80, b: 80, l: 20, r: 20 },
              plot_bgcolor: '#fff',
              paper_bgcolor: '#fff',
              autosize: true,
            }}
            className="h-[320px] w-full"
            style={{ width: '100%', maxWidth: '100%' }}
            config={{ responsive: true, displayModeBar: false }}
            useResizeHandler={true}
          />
        </div>
      </div>

      {/* Skills Growth Chart */}
      <div
        className="
          border-[#E2E8F0] border bg-white px-4 hover:border-[#020817] transition-all
          cursor-pointer rounded-2xl w-full mb-4
        "
      >
        {skillChartData.length > 0 ? (
          <Plot
            data={skillChartData}
            layout={{
              title: {
                text: 'Skills Growth Over Time',
                x: 0,
                xanchor: 'left',
                font: { size: 18, family: 'Inter, system-ui, sans-serif' },
              },
              showlegend: true,
              legend: {
                orientation: 'h',
                yanchor: 'bottom',
                y: -0.35,
                xanchor: 'center',
                x: 0.5,
              },
              margin: { t: 80, b: 120, l: 60, r: 20 },
              plot_bgcolor: '#fff',
              paper_bgcolor: '#fff',
              autosize: true,
              yaxis: {
                title: { text: 'Class Avg Correct (%)', font: { size: 12 } },
                range: [0, 100],
              },
              xaxis: {
                title: { text: 'Week', font: { size: 12 } },
              },
            }}
            className="h-[400px] w-full"
            style={{ width: '100%', maxWidth: '100%' }}
            config={{ responsive: true, displayModeBar: false }}
            useResizeHandler={true}
          />
        ) : (
          <div className="flex flex-col justify-center items-center py-10">
            <MessageCircleX color="#999" size={60} />
            <p className="text-[14px] font-medium text-appTextLight mt-3">
              No skills growth data available
            </p>
          </div>
        )}
      </div>

      {/* Learning Outcomes Bar Chart */}
      {learningOutcomesData.length > 0 && (
        <div className="border-[#E2E8F0] border bg-white px-4 hover:border-[#020817] transition-all cursor-pointer rounded-2xl w-full mb-4">
          <Plot
            data={[
              {
                type: 'bar',
                x: learningOutcomesData.map((o) => o.name),
                y: learningOutcomesData.map((o) => o.classAvgCorrectPct),
                marker: {
                  color: learningOutcomesData.map((o) =>
                    o.status === 'exceeded' ? '#10b981' : o.status === 'met' ? '#f59e0b' : '#ef4444'
                  ),
                },
                text: learningOutcomesData.map((o) => `${o.classAvgCorrectPct.toFixed(1)}%`),
                textposition: 'outside',
                hoverinfo: 'x+y',
              },
            ]}
            layout={{
              title: {
                text: 'Learning Outcomes',
                x: 0,
                xanchor: 'left',
                font: { size: 18, family: 'Inter, system-ui, sans-serif' },
              },
              yaxis: {
                title: { text: 'Class Avg Correct (%)', font: { size: 12 } },
                range: [0, 110],
              },
              margin: { t: 80, b: 120, l: 60, r: 20 },
              plot_bgcolor: '#fff',
              paper_bgcolor: '#fff',
              autosize: true,
            }}
            className="h-[400px] w-full"
            style={{ width: '100%', maxWidth: '100%' }}
            config={{ responsive: true, displayModeBar: false }}
            useResizeHandler={true}
          />
        </div>
      )}

      {/* Class Learning Gaps */}
      {classLearningGaps.hasGaps && (
        <div className="grid grid-cols-2 gap-4">
          {/* Skill Gaps */}
          <div className="border-[#E2E8F0] border bg-white rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 p-4 border-b border-[#E2E8F0]">
              <AlertTriangle className="text-orange-500" size={20} />
              <h3 className="font-semibold text-[16px]">Skill Gaps</h3>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              <div className="divide-y divide-[#E2E8F0]">
                {classLearningGaps.skills.length > 0 ? (
                  classLearningGaps.skills.map((item, index) => (
                    <div key={index} className="p-3 hover:bg-gray-50 border-l-4 border-l-orange-500">
                      <div className="flex justify-between items-center">
                        <p className="font-semibold text-[14px] text-[#020817]">{item.name}</p>
                        <span className="px-2 py-1 rounded text-[12px] font-medium bg-orange-100 text-orange-700">
                          {item.classAvgCorrectPct.toFixed(1)}% avg
                        </span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-orange-500"
                          style={{ width: `${item.classAvgCorrectPct}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-[13px] text-appTextLight text-center">
                    No skill gaps detected
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Outcome Gaps */}
          <div className="border-[#E2E8F0] border bg-white rounded-lg overflow-hidden">
            <div className="flex items-center gap-2 p-4 border-b border-[#E2E8F0]">
              <BookOpen className="text-red-500" size={20} />
              <h3 className="font-semibold text-[16px]">Outcome Gaps</h3>
            </div>
            <div className="max-h-[300px] overflow-y-auto">
              <div className="divide-y divide-[#E2E8F0]">
                {classLearningGaps.outcomes.length > 0 ? (
                  classLearningGaps.outcomes.map((item, index) => (
                    <div key={index} className="p-3 hover:bg-gray-50 border-l-4 border-l-red-500">
                      <div className="flex justify-between items-center">
                        <p className="font-semibold text-[14px] text-[#020817]">{item.name}</p>
                        <span className="px-2 py-1 rounded text-[12px] font-medium bg-red-100 text-red-700">
                          {item.classAvgCorrectPct.toFixed(1)}% avg
                        </span>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-red-500"
                          style={{ width: `${item.classAvgCorrectPct}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-[13px] text-appTextLight text-center">
                    No outcome gaps detected
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizLearning;
